import { throttle } from "lodash";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { Artefact } from "@/lib/artefacts";
import { getDescriptionArtefact } from "@/lib/artefacts/description";
import { syncChannel } from "@/lib/broadcast-channels";
import { api, type types } from "@/lib/openapi";
import { followLink } from "@/lib/openapi/helpers";
import router from "@/router";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "../composables/draft-manager";
import { useEditManager } from "../composables/edit-manager";
import { useProcessElementsStore } from "./process-elements-store";

export type Task = types.ProcessTask;

// Uses the current route to determine what the 'task' should be and fetches
// the task description for you.
export const useTaskStore = defineEntityStore("task", () => {
    const task = ref<Task>();
    const syncStore = useSyncStore();
    const entityId = computed(() => task.value?.identity);
    const uiCode = ref<Artefact<"text/javascript">>();
    const description = ref<Artefact<"text/markdown">>();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.TASK,
        attribute: "description",
        artefact: description,
    });

    const { refresh: refreshProcessElements } = useProcessElementsStore();

    discardDraftsAfterSync(entityId, [descriptionDraft], EntityType.TASK);

    const {
        organization,
        stek,
        process,
        task: taskInfo,
    } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );
    const isFetching = ref(false);
    const isFetchingDescription = ref(false);
    const isFetchingUiCode = ref(false);
    const controller = ref(new AbortController());

    async function updateTask(force = false) {
        const taskId = taskInfo.value?.identity;

        // In case the same task is selected, return early.
        if (!force && task.value && task.value.identity === taskId) {
            return;
        }

        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        if (!taskId) {
            description.value = undefined;
            task.value = undefined;
            return;
        }

        isFetching.value = true;
        description.value = undefined;
        uiCode.value = undefined;
        task.value = undefined;

        try {
            const { response, data } =
                await api.startProcessProcessElementMgtGetTaskStartPost({
                    body: {
                        task_id: `${taskId}`,
                        include_archived: true,
                        revision: releaseStore.activeRevision ?? undefined,
                    },
                    signal: controller.value.signal,
                });

            if (!response.ok) {
                if (response.status === 404) {
                    if (!releaseStore.activeRevision) {
                        router.replace({
                            name: "not-found",
                            params: {
                                orgName: organization.value?.name,
                                stekName: stek.value?.name,
                            },
                            query: {
                                type: "task",
                                id: taskId,
                            },
                        });
                    }
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            task.value = data;
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        } finally {
            isFetching.value = false;
        }
        if (task.value) {
            await Promise.all([
                updateDescription(taskId),
                updateUiCode(taskId),
            ]);
        }
    }

    // Watch for task identity changes
    watch(
        () => taskInfo.value?.identity,
        () => updateTask(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (task.value) updateTask(true);
        }
    );

    // Listen for sync events that might affect the task
    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) =>
                            identity.attribute === "process_definition"
                    ) &&
                    task.value
                ) {
                    updateTask(true);
                }
            });
        }
    });

    const updateDescription = async (currentTaskId?: string | undefined) => {
        if (!task.value) {
            return;
        }

        if (!description.value) {
            isFetchingDescription.value = true;
            try {
                const descriptionResult = await getDescriptionArtefact(
                    task.value,
                    "taskDescription",
                    controller.value.signal,
                    revisionQuery.value
                );

                // Check if task has changed during the async operation
                if (!task.value || task.value.identity !== currentTaskId) {
                    console.debug(
                        "Task changed during description fetch, aborting description update"
                    );
                    return;
                }

                description.value = descriptionResult;
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    // Request was aborted by the AbortController.
                    return;
                } else {
                    throw e;
                }
            } finally {
                isFetchingDescription.value = false;
            }
        }
    };

    const updateUiCode = async (currentTaskId?: string | undefined) => {
        if (!task.value) {
            return;
        }

        if (!uiCode.value) {
            if (task.value.ui_code) {
                try {
                    isFetchingUiCode.value = true;
                    const { response: uiResponse, data } =
                        await followLink<File>(
                            task.value.ui_code,
                            "get",
                            undefined,
                            controller.value.signal,
                            revisionQuery.value
                        );

                    if (!uiResponse.ok) {
                        return;
                    }

                    // Check if task has changed during the async operation
                    if (!task.value || task.value.identity !== currentTaskId) {
                        console.debug(
                            "Task changed during UI code fetch, aborting UI code update"
                        );
                        return;
                    }

                    const blob =
                        data instanceof Blob ? data : new Blob([data || ""]);

                    // Creating an Artefact involves async operations (blob.text()),
                    // check again for task change
                    const content = await blob.text();

                    // Check again if task has changed during content processing
                    if (!task.value || task.value.identity !== currentTaskId) {
                        console.debug(
                            "Task changed during UI code processing, aborting UI code update"
                        );
                        return;
                    }

                    uiCode.value = new Artefact({
                        name: "taskUiCode",
                        identity: task.value.ui_code.identity,
                        blob: blob,
                        mimetype: blob.type as "text/javascript",
                        content: content,
                    });
                } catch (e) {
                    if (e instanceof DOMException && e.name === "AbortError") {
                        // Request was aborted by the AbortController.
                        return;
                    } else {
                        throw e;
                    }
                } finally {
                    isFetchingUiCode.value = false;
                }
            } else {
                // Check if task has changed before setting an empty artefact
                if (!task.value || task.value.identity !== currentTaskId) {
                    console.debug(
                        "Task changed before creating empty artefact, aborting"
                    );
                    return;
                }

                uiCode.value = Artefact.createEmptyArtefact({
                    mimetype: "text/javascript",
                    identity: task.value.identity + "_ui_code",
                });
            }
        }
    };

    const isModified = computed(() => {
        if (isFetching.value) {
            return false;
        }

        return Boolean(
            description.value?.isModified || uiCode.value?.isModified
        );
    });

    const queueSave = async () => {
        if (!isModified.value || !task.value) {
            return;
        }

        const updateContent: types.StartProcessProcessElementMgtUpdateTaskStartPostData =
            {
                body: {
                    task_id: task.value.identity,
                },
            };

        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (uiCode.value?.isModified) {
            updateContent.body.ui_code = uiCode.value.blob;
        }

        if (
            !updateContent.body.task_id ||
            Object.keys(updateContent.body).length === 1
        ) {
            // Only task_id is set, no need to update.
            return;
        }

        syncStore.queueSync({
            entityType: EntityType.TASK,
            args: [updateContent],
        });

        if (description.value?.isModified) {
            description.value.persist();
        }

        if (uiCode.value?.isModified) {
            uiCode.value.persist();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        await uiCode.value?.resetContent();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    syncChannel.addEventListener(
        "message",
        ({ data: { message, identity } }) => {
            if (
                message === "synced" &&
                identity.organizationId === organization.value?.identity &&
                identity.entityType === EntityType.TASK &&
                identity.entityId === task.value?.identity
            ) {
                updateTask(true);
            }
        }
    );

    const deleteTask = async () => {
        if (!task.value) {
            return;
        }
        if (!task.value.is_archived) {
            throw Error("Can only delete archived tasks");
        }

        await api.startProcessProcessElementMgtDeleteTaskStartPost({
            body: {
                task_id: task.value.identity,
            },
        });

        await refreshProcessElements();

        if (organization.value && stek.value && process.value) {
            await router.push({
                name: "processPage",
                params: {
                    orgName: organization.value.name,
                    stekName: stek.value.name,
                    processId: process.value.identity,
                },
            });
        }
    };

    return {
        task,
        description,
        uiCode,
        isModified,
        isFetching: computed(() => {
            return (
                isFetching.value ||
                isFetchingDescription.value ||
                isFetchingUiCode.value
            );
        }),
        reset,
        deleteTask,
    };
});

export const useTaskManager = () => storeToRefs(useTaskStore());
